import React, { useState, useEffect } from 'react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import CustomSelect from '../ui/CustomSelect';
import { launchBacktestRun, getBacktestConfig, listBacktestConfigs } from '../../api/botsApi';
import { TIMEFRAMES } from '../../lib/timeframes';
import { APP_ACTION_SURFACES } from '../../access/surfaces';
import { useActionAccess } from '../../access/actionAccess';

const LaunchBacktestModal = ({ botVersionId, backtestConfigId, onSuccess, onClose }) => {
  const launchBacktestAccess = useActionAccess(APP_ACTION_SURFACES.botVersionLaunchBacktest);
  const [configs, setConfigs] = useState([]);
  const [selectedConfigId, setSelectedConfigId] = useState(backtestConfigId || '');
  const [configDetails, setConfigDetails] = useState(null);

  const [instrumentSymbol, setInstrumentSymbol] = useState('');
  const [dataWindowStart, setDataWindowStart] = useState('');
  const [dataWindowEnd, setDataWindowEnd] = useState('');
  const [randomSeed, setRandomSeed] = useState('');
  const [timeframe, setTimeframe] = useState('');
  const [description, setDescription] = useState('');
  // Spread/Commission toggles & overrides
  const [includeSpread, setIncludeSpread] = useState(true);
  const [includeCommission, setIncludeCommission] = useState(true);
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [spreadPips, setSpreadPips] = useState(''); // optional override
  const [commissionUnits, setCommissionUnits] = useState(''); // PER_TRADE | PER_LOT
  const [commissionPerUnit, setCommissionPerUnit] = useState(''); // decimal
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  const timeframeOptions = [
    { value: '', label: configDetails?.default_timeframe ? `(Use default: ${configDetails.default_timeframe})` : '(Select timeframe)' },
    ...TIMEFRAMES.map(tf => ({ value: tf, label: tf }))
  ];

  // Fetch configs for the selected version
  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        if (!botVersionId) return;
  const list = await listBacktestConfigs(botVersionId);
  if (!mounted) return;
  // listBacktestConfigs now returns a flat array; fallback guard just in case
  const items = Array.isArray(list) ? list : (Array.isArray(list?.results) ? list.results : []);
  setConfigs(items);
        // Preselect: prop -> existing selection -> first item
  const preset = backtestConfigId || selectedConfigId || (items?.[0]?.id || '');
        setSelectedConfigId(preset);
      } catch (e) {
        console.error('Failed to load backtest configs for version', botVersionId, e);
        if (mounted) setConfigs([]);
      }
    })();
    return () => { mounted = false; };
  }, [botVersionId]);

  // Load selected config details to show slippage (and any other read-only fields)
  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        if (!selectedConfigId) { setConfigDetails(null); return; }
        const cfg = await getBacktestConfig(selectedConfigId);
        if (mounted) setConfigDetails(cfg);
      } catch (e) {
        console.warn('Failed to load config details', e);
        if (mounted) setConfigDetails(null);
      }
    })();
    return () => { mounted = false; };
  }, [selectedConfigId]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!launchBacktestAccess.ensureAccess()) {
      return;
    }
    setIsLoading(true);
    setError(null);

    if (!selectedConfigId) {
      setError('Please select a Backtest Config.');
      setIsLoading(false);
      return;
    }
    if (!instrumentSymbol || !dataWindowStart || !dataWindowEnd) {
      setError('Instrument Symbol, Start Date, and End Date are required.');
      setIsLoading(false);
      return;
    }

    const startIso = new Date(dataWindowStart).toISOString();
    const endIso = new Date(dataWindowEnd).toISOString();
    if (new Date(startIso) >= new Date(endIso)) {
      setError('Start must be before End.');
      setIsLoading(false);
      return;
    }

    const launchData = {
      config_id: selectedConfigId,
      timeframe: timeframe || configDetails?.default_timeframe,
      data_window: { from: startIso.substring(0,10), to: endIso.substring(0,10) },
      instrument_symbol: instrumentSymbol,
      bot_version_id: botVersionId, // included for legacy fallback
      random_seed: randomSeed !== '' ? Number(randomSeed) : undefined,
      description: description || undefined,
      // Execution profile toggles & optional overrides
      include_spread: includeSpread,
      include_commission: includeCommission,
      ...(spreadPips !== '' ? { spread_pips: Number(spreadPips) } : {}),
      ...(commissionUnits ? { commission_units: commissionUnits } : {}),
      ...(commissionPerUnit !== '' ? { commission_per_unit: Number(commissionPerUnit) } : {}),
    };

    if (!launchData.timeframe) {
      setError('Select a timeframe (config has no default).');
      setIsLoading(false);
      return;
    }

    try {
      const result = await launchBacktestRun(launchData);
      onSuccess && onSuccess(result);
      onClose();
    } catch (err) {
      console.error('Failed to launch backtest:', err);
      setError(err.message || `Failed to launch backtest. ${err.detail || ''}`);
    } finally {
      setIsLoading(false);
    }
  };

  const configOptions = configs.map((c) => ({
    value: c.id,
    label: [
      (c.name || c.label || `${c.id.substring(0, 8)}...`),
      c.default_timeframe ? `· def ${c.default_timeframe}` : ''
    ].filter(Boolean).join(' '),
  }));

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-backgroundSurface p-6 rounded-lg shadow-xl w-full max-w-md">
        <h2 className="text-xl font-semibold text-textPrimary mb-4">Launch New Backtest</h2>
        <form onSubmit={handleSubmit} className="space-y-4">
          {error && <p className="text-destructive">{error}</p>}
          {launchBacktestAccess.disabled && <p className="text-sm text-textSecondary">{launchBacktestAccess.blockedMessage}</p>}

          <div className="text-sm text-textSecondary space-y-1">
            <p>Bot Version ID: {botVersionId.substring(0, 8)}...</p>
          </div>

          <div>
            <label className="block text-sm font-medium text-textPrimary">Backtest Config</label>
            {configOptions.length > 0 ? (
              <CustomSelect
                options={configOptions}
                value={selectedConfigId}
                onChange={setSelectedConfigId}
                placeholder="Select a config"
                searchable
                searchPlaceholder="Search configs…"
                className="mt-1"
              />
            ) : (
              <p className="text-sm text-textSecondary mt-1">No configs found. Create one from the Version page.</p>
            )}
          </div>

          {/* Slippage fields from the selected config (read-only); risk_json hidden intentionally */}
          {configDetails && (
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label htmlFor="slippageMs" className="block text-sm font-medium text-textPrimary">Slippage (ms)</label>
                <Input id="slippageMs" type="number" value={configDetails.slippage_ms ?? ''} readOnly className="mt-1" />
              </div>
              <div>
                <label htmlFor="slippageR" className="block text-sm font-medium text-textPrimary">Slippage (R)</label>
                <Input id="slippageR" type="number" value={configDetails.slippage_r ?? ''} readOnly className="mt-1" />
              </div>
            </div>
          )}

          <div>
            <label htmlFor="instrumentSymbol" className="block text-sm font-medium text-textPrimary">Instrument Symbol</label>
            <Input id="instrumentSymbol" type="text" value={instrumentSymbol} onChange={(e) => setInstrumentSymbol(e.target.value.toUpperCase())} placeholder="e.g., EURUSD" required className="mt-1" />
          </div>

          {/* Execution profile toggles */}
          <div className="mt-2 p-3 border rounded-md bg-backgroundBody">
            <div className="flex items-center justify-between">
              <div>
                <div className="text-sm font-medium text-textPrimary">Execution Costs</div>
                <div className="text-[11px] text-textSecondary">When toggles are ON and overrides are empty, backend uses instrument defaults.</div>
              </div>
            </div>
            <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-3">
              <label className="flex items-center gap-2 text-sm">
                <input type="checkbox" checked={includeSpread} onChange={(e) => setIncludeSpread(e.target.checked)} />
                Include Spread
              </label>
              <label className="flex items-center gap-2 text-sm">
                <input type="checkbox" checked={includeCommission} onChange={(e) => setIncludeCommission(e.target.checked)} />
                Include Commission
              </label>
            </div>

            <button
              type="button"
              onClick={() => setShowAdvanced(s => !s)}
              className="mt-3 text-xs text-primary hover:underline"
            >
              {showAdvanced ? 'Hide' : 'Show'} Advanced Overrides
            </button>

            {showAdvanced && (
              <div className="mt-3 grid grid-cols-1 sm:grid-cols-3 gap-3">
                <div>
                  <label className="block text-sm font-medium text-textPrimary">Spread (pips)</label>
                  <Input type="number" step="0.1" min="0" value={spreadPips} onChange={(e) => setSpreadPips(e.target.value)} placeholder="e.g., 1.5" className="mt-1" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-textPrimary">Commission Units</label>
                  <CustomSelect
                    options={[{ value: '', label: '(Use default)' }, { value: 'PER_TRADE', label: 'PER_TRADE' }, { value: 'PER_LOT', label: 'PER_LOT' }]}
                    value={commissionUnits}
                    onChange={setCommissionUnits}
                    className="mt-1"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-textPrimary">Commission per Unit</label>
                  <Input type="number" step="0.01" min="0" value={commissionPerUnit} onChange={(e) => setCommissionPerUnit(e.target.value)} placeholder="e.g., 3.5" className="mt-1" />
                </div>
              </div>
            )}
          </div>

          <div>
            <label htmlFor="timeframe" className="block text-sm font-medium text-textPrimary">Timeframe</label>
            <CustomSelect
              id="timeframe"
              options={timeframeOptions}
              value={timeframe}
              onChange={setTimeframe}
              placeholder={configDetails?.default_timeframe ? `Default ${configDetails.default_timeframe}` : 'Select timeframe'}
              className="mt-1"
            />
            <p className="text-[11px] text-textSecondary mt-1">Choose a timeframe or leave on the default option to use the config default.</p>
          </div>

          <div>
            <label htmlFor="dataWindowStart" className="block text-sm font-medium text-textPrimary">Data Window Start</label>
            <Input id="dataWindowStart" type="datetime-local" value={dataWindowStart} onChange={(e) => setDataWindowStart(e.target.value)} required className="mt-1" />
          </div>

          <div>
            <label htmlFor="dataWindowEnd" className="block text-sm font-medium text-textPrimary">Data Window End</label>
            <Input id="dataWindowEnd" type="datetime-local" value={dataWindowEnd} onChange={(e) => setDataWindowEnd(e.target.value)} required className="mt-1" />
          </div>

          <div>
            <label htmlFor="randomSeed" className="block text-sm font-medium text-textPrimary">Random Seed (optional)</label>
            <Input id="randomSeed" type="number" value={randomSeed} onChange={(e) => setRandomSeed(e.target.value)} placeholder="e.g., 42" className="mt-1" />
          </div>

          <div>
            <label htmlFor="description" className="block text-sm font-medium text-textPrimary">Description (optional)</label>
            <Input id="description" type="text" value={description} maxLength={140} onChange={(e) => setDescription(e.target.value)} placeholder="Brief description for this run" className="mt-1" />
            <p className="text-[11px] text-textSecondary mt-1">Helps you identify the run later. 140 chars max.</p>
          </div>

          <div className="flex justify-end space-x-3 mt-6">
            <Button type="button" variant="secondary" onClick={onClose} disabled={isLoading}>Cancel</Button>
            <Button
              type="submit"
              variant="primary"
              disabled={isLoading || !selectedConfigId || configs.length === 0 || launchBacktestAccess.disabled}
              title={launchBacktestAccess.disabled ? launchBacktestAccess.blockedMessage : undefined}
            >
              {isLoading ? 'Launching...' : 'Launch Backtest'}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default LaunchBacktestModal;
